import React, { useState, useEffect } from 'react';
import { reportsAPI, teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import TeamMemberSearchSelect from '../components/TeamMemberSearchSelect';
import {
  FileText,
  Users,
  Briefcase,
  Target,
  AlertTriangle,
  Search,
  Calendar,
  ChevronRight,
  Clock,
  Package,
  ClipboardList,
  Truck,
  TrendingUp,
  Banknote,
  CheckCircle2,
  XCircle,
  UserX,
  Percent,
  FileSpreadsheet,
  FileDown,
} from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Reports = () => {
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState('attendance');
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  // Attendance Report State
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceFilters, setAttendanceFilters] = useState({
    member_id: '',
    start_date: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  });

  // Jobs Missing Resources State
  const [jobsData, setJobsData] = useState(null);
  const [daysAhead, setDaysAhead] = useState(8);

  // Leads Expiring State
  const [leadsData, setLeadsData] = useState(null);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsDaysAhead, setLeadsDaysAhead] = useState(7);

  // Sales Conversion State
  const [salesData, setSalesData] = useState(null);
  const [salesFilters, setSalesFilters] = useState({
    start_date: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  });

  const canView = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadTeamMembers();
  }, []);

  useEffect(() => {
    if (activeReport === 'attendance') loadAttendanceReport();
    if (activeReport === 'jobs') loadJobsReport();
    if (activeReport === 'leads') loadLeadsReport();
    if (activeReport === 'sales-conversion') loadSalesConversionReport();
  }, [activeReport]);

  const loadTeamMembers = async () => {
    try {
      const res = await teamMembersAPI.getAll();
      setTeamMembers(res.data.filter(m => m.status === 'active'));
    } catch (error) {
      console.error('Failed to load team members');
    }
  };

  const loadAttendanceReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getAttendanceExceptions({
        start_date: attendanceFilters.start_date,
        end_date: attendanceFilters.end_date,
        member_id: attendanceFilters.member_id || undefined,
      });
      setAttendanceData(res.data);
    } catch (error) {
      toast.error('Failed to load attendance report');
    } finally {
      setLoading(false);
    }
  };

  const loadJobsReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getJobsMissingResources(daysAhead);
      setJobsData(res.data);
    } catch (error) {
      toast.error('Failed to load jobs report');
    } finally {
      setLoading(false);
    }
  };

  const loadLeadsReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getLeadsExpiring({
        days_ahead: leadsDaysAhead,
        search: leadsSearch || undefined,
      });
      setLeadsData(res.data);
    } catch (error) {
      toast.error('Failed to load leads report');
    } finally {
      setLoading(false);
    }
  };

  const loadSalesConversionReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getSalesConversion({
        start_date: salesFilters.start_date,
        end_date: salesFilters.end_date,
      });
      setSalesData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load sales conversion report');
    } finally {
      setLoading(false);
    }
  };

  const buildSalesConversionRows = () => {
    if (!salesData?.metrics) return [];
    const m = salesData.metrics;
    return [
      ['Total Quote Value of Leads', formatCurrency(m.total_quote_value)],
      ['Total No. of Leads', m.total_leads],
      ['Leads Converted to Jobs (Closed Won)', m.converted_leads_count],
      ['Value of Converted Leads', formatCurrency(m.converted_leads_value)],
      ['Leads Lost (Closed Lost)', m.lost_leads_count],
      ['Value of Lost Leads', formatCurrency(m.lost_leads_value)],
      ['Unassigned Leads', m.unassigned_leads_count],
      ['Leads Generated in Period', m.leads_generated_in_period],
      ['Conversion Rate', `${m.conversion_rate_pct}%`],
    ];
  };

  const exportSalesConversionExcel = () => {
    if (!salesData?.metrics) {
      toast.error('Generate the report first');
      return;
    }
    const rows = buildSalesConversionRows();
    const sheetData = [
      ['MBConstruction — Sales Conversion Report'],
      [`Period: ${salesFilters.start_date} to ${salesFilters.end_date}`],
      [`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`],
      [],
      ['Metric', 'Value'],
      ...rows,
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws['!cols'] = [{ wch: 42 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Conversion');
    const filename = `sales-conversion_${salesFilters.start_date}_to_${salesFilters.end_date}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Excel exported');
  };

  const exportSalesConversionPDF = () => {
    if (!salesData?.metrics) {
      toast.error('Generate the report first');
      return;
    }
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text('Sales Conversion Report', 40, 50);

    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.text('MBConstruction — Maintenance Management System', 40, 68);

    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105); // slate-600
    doc.text(`Period: ${salesFilters.start_date} to ${salesFilters.end_date}`, 40, 90);
    doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, 40, 105);

    // Horizontal rule
    doc.setDrawColor(226, 232, 240);
    doc.line(40, 115, pageWidth - 40, 115);

    // Metrics table
    autoTable(doc, {
      startY: 130,
      head: [['Metric', 'Value']],
      body: buildSalesConversionRows(),
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 11, halign: 'left' },
      bodyStyles: { fontSize: 10, textColor: [30, 41, 59] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 320 },
        1: { cellWidth: 180, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 40, right: 40 },
    });

    // Footer
    const finalY = doc.lastAutoTable?.finalY || 200;
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text(
      'Confidential — for internal management use only.',
      40,
      finalY + 30
    );

    const filename = `sales-conversion_${salesFilters.start_date}_to_${salesFilters.end_date}.pdf`;
    doc.save(filename);
    toast.success('PDF exported');
  };

  if (!canView) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Restricted</h2>
        <p className="text-slate-600">You do not have permission to view reports.</p>
      </div>
    );
  }

  const reports = [
    { id: 'attendance', name: 'Attendance Exceptions', icon: Users, description: 'Team members not clocked in by 10am' },
    { id: 'jobs', name: 'Jobs Missing Resources', icon: Briefcase, description: 'Jobs starting soon without complete setup' },
    { id: 'leads', name: 'Expiring Leads', icon: Target, description: 'Leads requiring follow-up soon' },
    { id: 'sales-conversion', name: 'Sales Conversion', icon: TrendingUp, description: 'Lead → job conversion metrics for a date range' },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Reports
        </h1>
        <p className="text-slate-600">View operational reports and exception alerts</p>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {reports.map((report) => (
          <button
            key={report.id}
            onClick={() => setActiveReport(report.id)}
            className={`p-4 rounded-xl border text-left transition-all ${
              activeReport === report.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                activeReport === report.id ? 'bg-blue-100' : 'bg-slate-100'
              }`}>
                <report.icon className={`w-5 h-5 ${
                  activeReport === report.id ? 'text-blue-600' : 'text-slate-600'
                }`} />
              </div>
              <div>
                <p className={`font-medium ${activeReport === report.id ? 'text-blue-900' : 'text-slate-900'}`}>
                  {report.name}
                </p>
                <p className={`text-sm ${activeReport === report.id ? 'text-blue-600' : 'text-slate-500'}`}>
                  {report.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Attendance Exceptions Report */}
      {activeReport === 'attendance' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members Not At Work
            </h2>
            <p className="text-sm text-slate-500">Members who did not clock in by 10:00 AM</p>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Team Member</label>
                <TeamMemberSearchSelect
                  members={teamMembers}
                  selectedMemberId={attendanceFilters.member_id}
                  onSelect={(id) => setAttendanceFilters({ ...attendanceFilters, member_id: id })}
                  placeholder="All members..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={attendanceFilters.start_date}
                  onChange={(e) => setAttendanceFilters({ ...attendanceFilters, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={attendanceFilters.end_date}
                  onChange={(e) => setAttendanceFilters({ ...attendanceFilters, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={loadAttendanceReport}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:bg-blue-300"
                >
                  {loading ? 'Loading...' : 'Generate Report'}
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="p-4">
            {attendanceData ? (
              <>
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {attendanceData.total_exceptions} exception(s) found
                  </p>
                </div>

                {attendanceData.exceptions.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Date</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Team Member</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Description</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {attendanceData.exceptions.map((exc) => (
                        <tr key={exc.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">{exc.date}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{exc.member_name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{exc.description}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              exc.resolved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {exc.resolved ? 'Resolved' : 'Unresolved'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No attendance exceptions found for this period</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Click Generate Report to view data</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Jobs Missing Resources Report */}
      {activeReport === 'jobs' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Jobs Missing Resources
            </h2>
            <p className="text-sm text-slate-500">Jobs starting soon without team members, subcontractors, BOQ, or requisitions</p>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Days Ahead</label>
                <select
                  value={daysAhead}
                  onChange={(e) => setDaysAhead(parseInt(e.target.value))}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value={3}>Next 3 days</option>
                  <option value={7}>Next 7 days</option>
                  <option value={8}>Next 8 days</option>
                  <option value={14}>Next 14 days</option>
                  <option value={30}>Next 30 days</option>
                </select>
              </div>
              <button
                onClick={loadJobsReport}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:bg-blue-300"
              >
                {loading ? 'Loading...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="p-4">
            {jobsData ? (
              <>
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {jobsData.total_jobs_with_issues} job(s) with missing resources
                  </p>
                </div>

                {jobsData.jobs.length > 0 ? (
                  <div className="space-y-3">
                    {jobsData.jobs.map((job) => (
                      <Link
                        key={job.job_id}
                        to={`/jobs/${job.job_id}`}
                        className="block p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              {job.job_code && (
                                <span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  {job.job_code}
                                </span>
                              )}
                              <span className="font-medium text-slate-900">{job.title}</span>
                            </div>
                            <p className="text-sm text-slate-500">
                              Scheduled: {job.scheduled_date ? format(new Date(job.scheduled_date), 'MMM dd, yyyy') : 'Not set'}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {job.issues.map((issue, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-red-50 text-red-700 rounded">
                                  {issue.includes('team') && <Users className="w-3 h-3" />}
                                  {issue.includes('subcontractor') && <Truck className="w-3 h-3" />}
                                  {issue.includes('BOQ') && <ClipboardList className="w-3 h-3" />}
                                  {issue.includes('requisition') && <Package className="w-3 h-3" />}
                                  {issue}
                                </span>
                              ))}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-8 h-8 mx-auto mb-2 text-green-300" />
                    <p>All upcoming jobs have complete resource allocation!</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Click Generate Report to view data</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leads Expiring Report */}
      {activeReport === 'leads' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Expiring Leads
            </h2>
            <p className="text-sm text-slate-500">Leads requiring follow-up that have not been finalized</p>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={leadsSearch}
                    onChange={(e) => setLeadsSearch(e.target.value)}
                    placeholder="Search leads..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Days Ahead</label>
                <select
                  value={leadsDaysAhead}
                  onChange={(e) => setLeadsDaysAhead(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value={3}>Next 3 days</option>
                  <option value={7}>Next 7 days</option>
                  <option value={14}>Next 14 days</option>
                  <option value={30}>Next 30 days</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={loadLeadsReport}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:bg-blue-300"
                >
                  {loading ? 'Loading...' : 'Generate Report'}
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="p-4">
            {leadsData ? (
              <>
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {leadsData.total_expiring_leads} lead(s) expiring within {leadsData.days_ahead} days
                  </p>
                </div>

                {leadsData.leads.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Lead</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Client</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Days Until Expiry</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {leadsData.leads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            {lead.lead_code && (
                              <span className="text-xs font-mono text-blue-600 mr-2">{lead.lead_code}</span>
                            )}
                            <span className="font-medium text-slate-900">{lead.title}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{lead.client_name}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {lead.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${
                              lead.days_until_expiry <= 0 ? 'text-red-600' :
                              lead.days_until_expiry <= 3 ? 'text-amber-600' :
                              'text-slate-600'
                            }`}>
                              {lead.days_until_expiry <= 0 ? 'Overdue' : `${lead.days_until_expiry} days`}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/leads/${lead.id}`}
                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Target className="w-8 h-8 mx-auto mb-2 text-green-300" />
                    <p>No expiring leads found!</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Click Generate Report to view data</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sales Conversion Report */}
      {activeReport === 'sales-conversion' && (
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="sales-conversion-report">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Sales Conversion
            </h2>
            <p className="text-sm text-slate-500">Lead pipeline and conversion metrics for the selected period</p>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
                <input
                  data-testid="sales-conversion-start-date"
                  type="date"
                  value={salesFilters.start_date}
                  onChange={(e) => setSalesFilters({ ...salesFilters, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
                <input
                  data-testid="sales-conversion-end-date"
                  type="date"
                  value={salesFilters.end_date}
                  onChange={(e) => setSalesFilters({ ...salesFilters, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <button
                  data-testid="sales-conversion-generate-btn"
                  onClick={loadSalesConversionReport}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
                >
                  {loading ? 'Loading…' : 'Generate Report'}
                </button>
              </div>
            </div>

            {/* Export actions — visible once data is loaded */}
            {salesData?.metrics && (
              <div className="mt-3 flex flex-wrap gap-2 justify-end" data-testid="sales-conversion-export-bar">
                <button
                  data-testid="sales-conversion-export-excel"
                  onClick={exportSalesConversionExcel}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg shadow-sm transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </button>
                <button
                  data-testid="sales-conversion-export-pdf"
                  onClick={exportSalesConversionPDF}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg shadow-sm transition-colors"
                >
                  <FileDown className="w-4 h-4" />
                  Export PDF
                </button>
              </div>
            )}
          </div>

          {/* KPI Dashboard */}
          <div className="p-4">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Loading…</div>
            ) : salesData?.metrics ? (
              <>
                <p className="text-xs text-slate-500 mb-4">
                  Showing data from <span className="font-medium text-slate-700">{salesFilters.start_date}</span> to <span className="font-medium text-slate-700">{salesFilters.end_date}</span>
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* 1. Total Quote Value */}
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4" data-testid="kpi-total-quote-value">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Banknote className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Pipeline</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Total Quote Value of Leads</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(salesData.metrics.total_quote_value)}</p>
                  </div>

                  {/* 2. Total no of leads */}
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4" data-testid="kpi-total-leads">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-indigo-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Volume</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Total No. of Leads</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{salesData.metrics.total_leads}</p>
                  </div>

                  {/* 3. Converted count */}
                  <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="kpi-converted-count">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">Closed Won</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Leads Converted to Jobs</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{salesData.metrics.converted_leads_count}</p>
                  </div>

                  {/* 4. Converted value */}
                  <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="kpi-converted-value">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Banknote className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">Won Revenue</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Value of Converted Leads</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(salesData.metrics.converted_leads_value)}</p>
                  </div>

                  {/* 5. Lost count */}
                  <div className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-white p-4" data-testid="kpi-lost-count">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                        <XCircle className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-red-600 font-medium">Closed Lost</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Leads Lost</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{salesData.metrics.lost_leads_count}</p>
                  </div>

                  {/* 6. Lost value */}
                  <div className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-white p-4" data-testid="kpi-lost-value">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                        <Banknote className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-red-600 font-medium">Lost Revenue</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Value of Lost Leads</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(salesData.metrics.lost_leads_value)}</p>
                  </div>

                  {/* 7. Unassigned */}
                  <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4" data-testid="kpi-unassigned-count">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                        <UserX className="w-5 h-5 text-amber-600" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-amber-600 font-medium">Open</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Unassigned Leads</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{salesData.metrics.unassigned_leads_count}</p>
                  </div>

                  {/* 8. Generated in period */}
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4" data-testid="kpi-generated-in-period">
                    <div className="flex items-start justify-between">
                      <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-slate-700" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Inflow</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">Leads Generated in Period</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{salesData.metrics.leads_generated_in_period}</p>
                  </div>
                </div>

                {/* Conversion rate banner */}
                <div className="mt-5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 flex items-center gap-4" data-testid="kpi-conversion-rate">
                  <div className="w-12 h-12 rounded-xl bg-white border border-blue-200 flex items-center justify-center">
                    <Percent className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider text-blue-700 font-medium">Conversion Rate</p>
                    <p className="text-3xl font-bold text-slate-900">{salesData.metrics.conversion_rate_pct}%</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {salesData.metrics.converted_leads_count} won of {salesData.metrics.total_leads} total leads in this period
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500" data-testid="sales-conversion-empty">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Select a date range and click Generate Report to view metrics</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
